/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import { useEffect } from 'react'
import { WidgetGuard } from 'features/types'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { createMetric } from 'utils/runSearchUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { getOptions } from 'utils/timelineUtil'
import { toFilter } from 'utils/widgetUtil'
import { MeasureType, ExtraInfo } from 'enums'
import { SocialWebTimelineComparisonWidget } from 'schemas/SocialWebTimelineComparisonWidgetSchema'
import { Dashboard } from 'schemas/DashboardSchema'
import { MetricOptions } from 'features/widget/types'
import { useAppDispatch } from 'app/hooks'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

const createSearches = (
  widgetSetting: SocialWebTimelineComparisonWidget,
  dashboard: Dashboard,
  pageConfigUser: any
) => {
  const { measureTypes, precision, topicInfos } = widgetSetting

  const option: MetricOptions = {}

  if (
    measureTypes!.includes(MeasureType.OVERALL_STAR_RATINGS_AVERAGE) ||
    measureTypes!.includes(MeasureType.OVERALL_SURVEY_SCORES_AVERAGE)
  ) {
    option.smoothing = false
  }

  const expandedMeasureTypes = [...measureTypes!]
  if (!measureTypes!.includes(MeasureType.MENTIONS)) {
    expandedMeasureTypes.push(MeasureType.MENTIONS)
  }

  option.extraInfo = ExtraInfo.TIMELINE_NOTES

  return topicInfos!.map((topic) =>
    createMetric(
      dashboard.id!,
      dashboard.name!,
      precision!,
      [topic.topicId!],
      toFilter(widgetSetting, pageConfigUser.timeZone),
      expandedMeasureTypes.flatMap((measure) => getMetricSeries(measure!)),
      getOptions(widgetSetting),
      option
    )
  )
}

const RunSearchTopicsEffect: WidgetGuard = ({ widgetId, children }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebTimelineComparisonWidget>(widgetId)
  const dashboard = useDashboardData()
  const pageConfigUser = usePageConfigUser()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboard!, pageConfigUser)

    if (searches.length > 0) {
      searches.sort((a, b) => a.priority - b.priority)
      dispatch(getWidgetForDashboard({ searches, widgetId, timestamp: refreshTimestamp }))
    }
  }, [widgetId, dispatch, refreshTimestamp, widgetSetting, dashboard, pageConfigUser])

  return children
}

export default RunSearchTopicsEffect
